<template>
  <v-dialog persistent
            no-click-animation
            scrollable
            content-class="c-dialog"
            v-bind="$attrs"
            :fullscreen="fullscreen"
            :width="width"
            :value="value">
    <v-card ref="card">
      <div class="c-dialog-titlebar">
        <div class="c-dialog-title" style="flex: 1 1 auto">
          {{ titleKey ? $t(titleKey) : titleText }}
          <span v-if="subtitleKey || subtitleText" class="c-dialog-subtitle">
            {{ subtitleKey ? $t(subtitleKey) : subtitleText }}
          </span>
        </div>
        <div style="flex: 0 0 auto">
          <v-btn v-if="fullscreenOption"
                 text
                 color="white"
                 class="px-5"
                 style="min-width:auto;height:100%;outline-offset:-2px !important;"
                 :title="fullscreen ? $t('cove.dialog.collapse') : $t('cove.dialog.expand')"
                 @click="toggleFullScreen">
            <v-icon>{{ fullscreenIcon }}</v-icon>
          </v-btn>
          <v-btn text
                 color="white"
                 class="px-5"
                 style="min-width:auto;height:100%;outline-offset:-2px !important;"
                 :title="$t('cove.general.close')"
                 @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>
      <v-card-text>
        <v-alert v-if="showErrors && errors.length > 0"
                 type="error"
                 class="ma-4"
                 transition="fade-transition">
          <ul>
            <li v-for="(error, i) in errors" :key="i">
              {{ error }}
            </li>
          </ul>
        </v-alert>
        <slot/>
      </v-card-text>
      <div class="c-dialog-actions" :class="{ 'grey lighten-5': !$vuetify.theme.dark }">
        <div>
          <slot name="actions-left"/>
        </div>
        <div class="flex-grow-1"></div>
        <div>
          <slot name="actions-right"/>
          <c-btn @click="close">
            {{ $t(closeKey) }}
          </c-btn>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  inheritAttrs: false,
  props: {
    titleKey: {
      type: String
    },
    titleText: {
      type: String
    },
    subtitleKey: {
      type: String
    },
    subtitleText: {
      type: String
    },
    closeKey: {
      type: String,
      default: 'cove.general.cancel'
    },
    value: {
      type: Boolean,
      required: true
    },
    width: {
      default: 800
    },
    manualClose: {
      type: Boolean,
      default: false
    },
    showErrors: {
      type: Boolean,
      default: false
    },
    errorScope: {
      type: String,
      default: null
    },
    fullscreenOption: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      manualFullScreen: false, // has the user manually taken control of the popup size
      manualFullScreenToggle: false, // the state of the manual popup size
      // define the listener here in data so it's unique per dialog instance
      escListener: (e) => {
        if (e.keyCode === 27)
        {
          this.close();
        }
      }
    };
  },
  computed: {
    fullscreen () {
      if (this.manualFullScreen)
      {
        return this.manualFullScreenToggle;
      }
      else
      {
        return this.$vuetify.breakpoint.smAndDown;
      }
    },
    fullscreenIcon () {
      return this.fullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen';
    },
    _width () {
      return isNaN(this.width) ? this.width : `${this.width}px`;
    },
    errors () {
      return this.$cove.validation.getAllErrorMessages(this.errorScope);
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (newValue) {
        if (newValue)
        {
          window.addEventListener('keydown', this.escListener);
        }
        else
        {
          window.removeEventListener('keydown', this.escListener);
        }
      }
    },
    fullscreen (newValue) {
      this.$emit('fullscreen', newValue);
    }
  },
  destroyed () {
    window.removeEventListener('keydown', this.escListener);
  },
  methods: {
    toggleFullScreen () {
      // determine the new state FIRST, then switch to manual mode
      this.manualFullScreenToggle = !this.fullscreen;
      this.manualFullScreen = true;
    },
    close () {
      // not crazy about this but the esc keydown handlers are called in the order they were added to the DOM, which is
      // the opposite of what we need. so this determines if this component is the topmost active dialog and only then
      // will it activate the close functionality.
      let cards = document.querySelectorAll('.c-dialog');
      let currentZ = parseInt(getComputedStyle(this.$refs.card.$el.parentNode.parentNode)['z-index']);

      let maxZ = 0;
      for (let i = 0; i < cards.length; i++) // ECRP-478 - use traditional for loop here
      {
        let d = cards[i];
        let parent = d.parentNode;
        if (parent.classList.contains('v-dialog__content--active'))
        {
          maxZ = Math.max(maxZ, parseInt(getComputedStyle(parent)['z-index']));
        }
      }

      if (currentZ === maxZ)
      {
        if (this.manualClose)
        {
          this.$emit('close');
        }
        else
        {
          this.$emit('input', false);
        }
      }
    }
  }
};
</script>